import _camelCase from 'lodash/camelCase';
import _capitalize from 'lodash/capitalize';
import moment from 'moment-timezone';
import pluralize from 'pluralize';
import store from '@/store';
import haversine from 'haversine';

export const clearVuexStorage = () => {
  const storageKeys = Object.keys(sessionStorage);
  storageKeys.forEach(key => {
    if (key.startsWith('vuex-')) {
      sessionStorage.removeItem(key);
    }
  });
};

// eslint-disable-next-line import/prefer-default-export
export const curlyReplace = (type, val, obj) =>
  val.replaceAll(/{{[^{}]+}}/g, m => {
    if (type === 'venue') {
      if (m === '{{address}}') return obj.address;
      if (m.match(/{{customField\|[0-9]+}}/)) return obj;
    }
    if (type === 'deal') {
      // Only shared replacements currently
    }
    if (type === 'product') {
      if (m === '{{venueName}}') return obj.venueName;
      if (m === '{{information}}') return obj.information;
      if (m === '{{shortInformation}}') return obj.short_information;
      if (m === '{{restrictions}}') return obj.restrictions;
      if (m === '{{frequency}}') return obj.frequency;
    }
    // Shared replacements [venue, deal, product]
    // Keep these last so a type can override these above and return
    if (m === '{{name}}') return obj.name;
    if (m === '{{description}}') return obj.description;
    if (m === '{{shortDescription}}') return obj.short_description;

    // No matched replacements - return empty string.
    return 'N/A';
  });

// Returns the current date on the server in UTC
export const getUTCDate = () => new Date().toISOString().slice(0, -1);

/**
 * Calendar date as Y-m-d for pass API query params (avoids Date → ISO Z shifting the day).
 * Handles Date instances (local calendar day), Y-m-d strings, and ISO strings (uses date prefix).
 */
export const toCalendarDateYmd = value => {
  if (value == null || value === '') {
    return value;
  }
  if (typeof value === 'string') {
    const isoPrefix = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoPrefix) {
      return isoPrefix[1];
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// The redeemed date at local time
export const getLocalDate = (dateString, format = 'MMM D, YYYY,  h:mm A') => {
  const timezone = store.state.partner?.timezone;

  return moment
    .utc(dateString)
    .tz(timezone)
    .format(format);
};

export const deepMerge = (target, source) => {
  const isObject = obj => obj && typeof obj === 'object';

  if (!isObject(target) || !isObject(source)) return source;

  Object.keys(source).forEach(key => {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue))
      target[key] = targetValue.concat(sourceValue);
    else if (isObject(targetValue) && isObject(sourceValue))
      target[key] = deepMerge({ ...targetValue }, sourceValue);
    else target[key] = sourceValue;
  });

  return target;
};

export const objectToArray = obj => (Array.isArray(obj) ? obj : [obj]);

export const camelCase = str => _camelCase(str);
export const pascalCase = str =>
  _camelCase(str)
    .charAt(0)
    .toUpperCase() + _camelCase(str).slice(1);
export const capitalize = str => _capitalize(str);

/**
 * Plain text of an HTML string, for hints and aria labels. Parsed rather than
 * regex-stripped so entities are decoded and attributes containing '>' do not
 * truncate the result. Parsing does not execute scripts.
 * @param {string} text
 * @returns {string}
 */
export const stripHtmlTags = (text = '') => {
  const safeText = text == null ? '' : String(text);
  if (!safeText) return '';
  const parsed = new DOMParser().parseFromString(safeText, 'text/html');
  parsed.body.querySelectorAll('script, style').forEach(el => el.remove());
  // Block boundaries have to become whitespace, otherwise `a<br>b` reads `ab`
  parsed.body
    .querySelectorAll('br, p, div, li, tr, td, th, h1, h2, h3, h4, h5, h6')
    .forEach(el => el.after(' '));
  return (parsed.body.textContent || '').replace(/\s+/g, ' ').trim();
};

/**
 * Display copy for a custom field. `label` is the partner-authored text and
 * `name` the internal fallback for fields saved without one. Returned as
 * entered: the casing is the partner's and must not be normalized. A label
 * holding only whitespace falls back to `name`; markup that renders no text
 * (a `<hr>` separator, say) is still a valid label.
 * @param {Object} field
 * @returns {string}
 */
export const getFieldDisplayLabel = field =>
  String(field?.label ?? '').trim() ? field.label : field?.name || '';

/**
 * Short plain-text name for a field, for validation messages. Uses the same
 * copy shown above the input rather than the internal `name` column, and
 * truncates so a long partner label stays readable as an error.
 * @param {Object} field
 * @param {number} maxLength
 * @returns {string}
 */
export const getFieldMessageLabel = (field, maxLength = 40) => {
  const text = stripHtmlTags(getFieldDisplayLabel(field)) || 'This field';
  return text.length > maxLength
    ? `${text.slice(0, maxLength).trimEnd()}…`
    : text;
};

export const applySortDisplay = (items, totalDisplayed, sortOrderIds) => {
  const itemsSortDisp = sortOrderIds
    .map(id => items.find(i => i.id === id))
    .filter(i => i);
  const itemsNotIn = items
    .filter(i => !sortOrderIds.some(sId => i.id === sId))
    .sort((a, b) =>
      String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
    );
  const allItems = itemsSortDisp.concat(itemsNotIn);
  return totalDisplayed >= 0 ? allItems.slice(0, totalDisplayed) : allItems;
};
/**
 * Finds the intersection of multiple arrays. If each array iten is an array of objects
 * we use the given key, or id if none is given.
 *
 * @param arrs - array of arrays to find the common items
 * @param key - if each sub array is that of objects we need to search by key
 * @returns {*[]}
 */
export const arrayIntersection = (arrs, key = 'id') => {
  const results = [];

  // Do we have an array of basic arrays or array of objects
  const isObject = typeof [...arrs][0][0] === 'object';

  if (arrs.length === 1) return arrs[0];

  const lists = [...arrs];
  lists.sort((a, b) => a.length - b.length);

  const currentList = lists[0];
  currentList.forEach(currentListItem => {
    if (!isObject) {
      if (lists.every(list => list.includes(currentListItem)))
        results.push(currentListItem);
    }

    if (isObject) {
      const inEvery = lists.every(
        list =>
          list.findIndex(listItem => listItem[key] === currentListItem[key]) >
          -1
      );
      if (inEvery) results.push(currentListItem);
    }
  });

  return results;
};

export const hexToRgb = hex => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
};

// set key in local storage, 1 hour expiry by default
export const setWithExpiry = (key, value, ttl = 3600000) => {
  const now = new Date();
  const item = {
    value,
    expiry: now.getTime() + ttl
  };
  localStorage.setItem(key, JSON.stringify(item));
};

export const getWithExpiry = key => {
  const itemStr = localStorage.getItem(key);
  // if the item doesn't exist, return null
  if (!itemStr) return null;

  const item = JSON.parse(itemStr);
  const now = new Date();
  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
};

export const getWeather = {
  windDirection: d => {
    if (typeof d !== 'number' || Number.isNaN(d)) return -1;

    // keep within the range: 0 <= d < 360
    // eslint-disable-next-line no-param-reassign
    d %= 360;

    if (d >= 11.25 && d < 33.75)
      return { words: 'north northeast', abbr: 'nne' };
    if (d >= 33.75 && d < 56.25) return { words: 'northeast', abbr: 'ne' };
    if (d >= 56.25 && d < 78.75)
      return { words: 'east northeast', abbr: 'ene' };
    if (d >= 78.75 && d < 101.25) return { words: 'east', abbr: 'e' };
    if (d >= 101.25 && d < 123.75)
      return { words: 'east southeast', abbr: 'ese' };
    if (d >= 123.75 && d < 146.25) return { words: 'southeast', abbr: 'se' };
    if (d >= 146.25 && d < 168.75)
      return { words: 'south southeast', abbr: 'sse' };
    if (d >= 168.75 && d < 191.25) return { words: 'south', abbr: 's' };
    if (d >= 191.25 && d < 213.75)
      return { words: 'south southwest', abbr: 'ssw' };
    if (d >= 213.75 && d < 236.25) return { words: 'southwest', abbr: 'sw' };
    if (d >= 236.25 && d < 258.75)
      return { words: 'west southwest', abbr: 'wsw' };
    if (d >= 258.75 && d < 281.25) return { words: 'west', abbr: 'w' };
    if (d >= 281.25 && d < 303.75)
      return { words: 'west northwest', abbr: 'wnw' };
    if (d >= 303.75 && d < 326.25) return { words: 'northwest', abbr: 'nw' };
    if (d >= 326.25 && d < 348.75)
      return { words: 'north northwest', abbr: 'nnw' };

    return { words: 'north', abbr: 'n' };
  }
};

export const venueDirectionLink = venue => {
  const {
    address,
    google_place_id: googlePlaceId,
    latitude,
    longitude
  } = venue;
  const venueAddress = address
    ? `&destination=${encodeURIComponent(address)}`
    : '';
  const venuePlaceId = googlePlaceId
    ? `&destination_place_id=${googlePlaceId}`
    : '';

  if (venueAddress && venuePlaceId)
    return `//www.google.com/maps/dir/?api=1${venueAddress}${venuePlaceId}`;

  if (latitude && longitude)
    return `//maps.google.com/?q=${latitude},${longitude}`;

  return false;
};

function gtmSend(paramObject) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(paramObject);
}

function gtmResetData() {
  if (window.dataLayer) {
    // eslint-disable-next-line func-names
    window.dataLayer.push(function() {
      this.reset();
    });
  }
}

export function gtmSendEvent(event, parameters = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event_parameters: undefined });
  window.dataLayer.push({ event: `bw_${event}`, event_parameters: parameters });
}

export function gtmPageView(route) {
  const params = {
    event: 'bw_page_view',
    partner_id: store.state.partner?.id?.toString(),
    offer_id: store.state.offer?.id?.toString(),
    is_preview: store.state.offer?.isPreview,
    preview_type: store.state.offer?.previewType,
    preview_id: store.state.offer?.previewTypeId,
    partner_user_id: store.state.partnerUser?.id?.toString(),
    route_name: route.name,
    product_id: route.params?.productId,
    deal_id: route.params?.dealId,
    venue_id: route.params?.venueId
  };
  if (!params.venue_id && params.product_id) {
    params.venue_id = store.getters['products/find'](
      params.product_id
    )?.venueId?.toString();
  }
  const paramsF = Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(params).filter(([_, v]) => v != null)
  );
  gtmResetData();
  gtmSend(paramsF);
}

export const getRandomId = () =>
  Math.random()
    .toString(36)
    .substring(2, 15) +
  Math.random()
    .toString(36)
    .substring(2, 15);

export const getLocalDeviceId = () => {
  let localDeviceId = localStorage.getItem('local_device_id');
  if (!localDeviceId) {
    localDeviceId = getRandomId();
    localStorage.setItem('local_device_id', localDeviceId);
  }
  return localDeviceId;
};

export const getSessionDeviceId = () => {
  let sessionDeviceId = sessionStorage.getItem('session_device_id');
  if (!sessionDeviceId) {
    sessionDeviceId = getRandomId();
    sessionStorage.setItem('session_device_id', sessionDeviceId);
  }
  return sessionDeviceId;
};

export const getVenueDistance = (venue, userLocation) => {
  if (
    venue?.latitude &&
    venue?.longitude &&
    userLocation?.latitude &&
    userLocation?.longitude
  ) {
    const unit = userLocation.userLocale === 'en-US' ? 'mile' : 'km';
    return haversine(
      {
        latitude: userLocation?.latitude || null,
        longitude: userLocation?.longitude || null
      },
      {
        latitude: Number(venue?.latitude) || null,
        longitude: Number(venue?.longitude) || null
      },
      { unit }
    );
  }
  return undefined;
};

export function formatDistance(distance) {
  if (distance == null) return '';
  if (distance > 100) return Math.round(distance).toLocaleString();
  return parseFloat(distance.toFixed(1)).toLocaleString();
}

export function isObjectEmpty(obj) {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}

export const mapsUrl = venue => {
  if (!venue) return null;
  const q = venue.latitude && venue.longitude
    ? `${venue.latitude},${venue.longitude}`
    : venue.address;
  return q ? `https://maps.google.com/?q=${encodeURIComponent(q)}` : null;
};

export function makePlural(str, arr) {
  if (typeof str !== 'string')
    throw new Error('First argument must be a string');

  if (!Array.isArray(arr)) throw new Error('Second argument must be an array');

  if (arr.length === 1) return str;

  return pluralize(str);
}

export function getCountryOptions() {
  return {
    US: 'United States',
    CA: 'Canada',
    AF: 'Afghanistan',
    AL: 'Albania',
    DZ: 'Algeria',
    DS: 'American Samoa',
    AD: 'Andorra',
    AO: 'Angola',
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AG: 'Antigua and Barbuda',
    AR: 'Argentina',
    AM: 'Armenia',
    AW: 'Aruba',
    AU: 'Australia',
    AT: 'Austria',
    AZ: 'Azerbaijan',
    BS: 'Bahamas',
    BH: 'Bahrain',
    BD: 'Bangladesh',
    BB: 'Barbados',
    BY: 'Belarus',
    BE: 'Belgium',
    BZ: 'Belize',
    BJ: 'Benin',
    BM: 'Bermuda',
    BT: 'Bhutan',
    BO: 'Bolivia',
    BA: 'Bosnia and Herzegovina',
    BW: 'Botswana',
    BV: 'Bouvet Island',
    BR: 'Brazil',
    IO: 'British Indian Ocean Territory',
    BN: 'Brunei Darussalam',
    BG: 'Bulgaria',
    BF: 'Burkina Faso',
    BI: 'Burundi',
    KH: 'Cambodia',
    CM: 'Cameroon',
    CV: 'Cape Verde',
    KY: 'Cayman Islands',
    CF: 'Central African Republic',
    TD: 'Chad',
    CL: 'Chile',
    CN: 'China',
    CX: 'Christmas Island',
    CC: 'Cocos (Keeling) Islands',
    CO: 'Colombia',
    KM: 'Comoros',
    CG: 'Congo',
    CK: 'Cook Islands',
    CR: 'Costa Rica',
    HR: 'Croatia (Hrvatska)',
    CU: 'Cuba',
    CY: 'Cyprus',
    CZ: 'Czech Republic',
    DK: 'Denmark',
    DJ: 'Djibouti',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    TP: 'East Timor',
    EC: 'Ecuador',
    EG: 'Egypt',
    SV: 'El Salvador',
    GQ: 'Equatorial Guinea',
    ER: 'Eritrea',
    EE: 'Estonia',
    ET: 'Ethiopia',
    FK: 'Falkland Islands (Malvinas)',
    FO: 'Faroe Islands',
    FJ: 'Fiji',
    FI: 'Finland',
    FR: 'France',
    FX: 'France, Metropolitan',
    GF: 'French Guiana',
    PF: 'French Polynesia',
    TF: 'French Southern Territories',
    GA: 'Gabon',
    GM: 'Gambia',
    GE: 'Georgia',
    DE: 'Germany',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GR: 'Greece',
    GL: 'Greenland',
    GD: 'Grenada',
    GP: 'Guadeloupe',
    GU: 'Guam',
    GT: 'Guatemala',
    GK: 'Guernsey',
    GN: 'Guinea',
    GW: 'Guinea-Bissau',
    GY: 'Guyana',
    HT: 'Haiti',
    HM: 'Heard and Mc Donald Islands',
    HN: 'Honduras',
    HK: 'Hong Kong',
    HU: 'Hungary',
    IS: 'Iceland',
    IN: 'India',
    ID: 'Indonesia',
    IR: 'Iran (Islamic Republic of)',
    IQ: 'Iraq',
    IE: 'Ireland',
    IM: 'Isle of Man',
    IL: 'Israel',
    IT: 'Italy',
    CI: 'Ivory Coast',
    JM: 'Jamaica',
    JP: 'Japan',
    JE: 'Jersey',
    JO: 'Jordan',
    KZ: 'Kazakhstan',
    KE: 'Kenya',
    KI: 'Kiribati',
    KP: "Korea, Democratic People's Republic of",
    KR: 'Korea, Republic of',
    XK: 'Kosovo',
    KW: 'Kuwait',
    KG: 'Kyrgyzstan',
    LA: "Lao People's Democratic Republic",
    LV: 'Latvia',
    LB: 'Lebanon',
    LS: 'Lesotho',
    LR: 'Liberia',
    LY: 'Libyan Arab Jamahiriya',
    LI: 'Liechtenstein',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    MO: 'Macau',
    MK: 'Macedonia',
    MG: 'Madagascar',
    MW: 'Malawi',
    MY: 'Malaysia',
    MV: 'Maldives',
    ML: 'Mali',
    MT: 'Malta',
    MH: 'Marshall Islands',
    MQ: 'Martinique',
    MR: 'Mauritania',
    MU: 'Mauritius',
    TY: 'Mayotte',
    MX: 'Mexico',
    FM: 'Micronesia, Federated States of',
    MD: 'Moldova, Republic of',
    MC: 'Monaco',
    MN: 'Mongolia',
    ME: 'Montenegro',
    MS: 'Montserrat',
    MA: 'Morocco',
    MZ: 'Mozambique',
    MM: 'Myanmar',
    NA: 'Namibia',
    NR: 'Nauru',
    NP: 'Nepal',
    NL: 'Netherlands',
    AN: 'Netherlands Antilles',
    NC: 'New Caledonia',
    NZ: 'New Zealand',
    NI: 'Nicaragua',
    NE: 'Niger',
    NG: 'Nigeria',
    NU: 'Niue',
    NF: 'Norfolk Island',
    MP: 'Northern Mariana Islands',
    NO: 'Norway',
    OM: 'Oman',
    PK: 'Pakistan',
    PW: 'Palau',
    PS: 'Palestine',
    PA: 'Panama',
    PG: 'Papua New Guinea',
    PY: 'Paraguay',
    PE: 'Peru',
    PH: 'Philippines',
    PN: 'Pitcairn',
    PL: 'Poland',
    PT: 'Portugal',
    PR: 'Puerto Rico',
    QA: 'Qatar',
    RE: 'Reunion',
    RO: 'Romania',
    RU: 'Russian Federation',
    RW: 'Rwanda',
    KN: 'Saint Kitts and Nevis',
    LC: 'Saint Lucia',
    VC: 'Saint Vincent and the Grenadines',
    WS: 'Samoa',
    SM: 'San Marino',
    ST: 'Sao Tome and Principe',
    SA: 'Saudi Arabia',
    SN: 'Senegal',
    RS: 'Serbia',
    SC: 'Seychelles',
    SL: 'Sierra Leone',
    SG: 'Singapore',
    SK: 'Slovakia',
    SI: 'Slovenia',
    SB: 'Solomon Islands',
    SO: 'Somalia',
    ZA: 'South Africa',
    GS: 'South Georgia South Sandwich Islands',
    ES: 'Spain',
    LK: 'Sri Lanka',
    SH: 'St. Helena',
    PM: 'St. Pierre and Miquelon',
    SD: 'Sudan',
    SR: 'Suriname',
    SJ: 'Svalbard and Jan Mayen Islands',
    SZ: 'Swaziland',
    SE: 'Sweden',
    CH: 'Switzerland',
    SY: 'Syrian Arab Republic',
    TW: 'Taiwan',
    TJ: 'Tajikistan',
    TZ: 'Tanzania, United Republic of',
    TH: 'Thailand',
    TG: 'Togo',
    TK: 'Tokelau',
    TO: 'Tonga',
    TT: 'Trinidad and Tobago',
    TN: 'Tunisia',
    TR: 'Turkey',
    TM: 'Turkmenistan',
    TC: 'Turks and Caicos Islands',
    TV: 'Tuvalu',
    UG: 'Uganda',
    UA: 'Ukraine',
    AE: 'United Arab Emirates',
    GB: 'United Kingdom',
    UM: 'United States minor outlying islands',
    UY: 'Uruguay',
    UZ: 'Uzbekistan',
    VU: 'Vanuatu',
    VA: 'Vatican City State',
    VE: 'Venezuela',
    VN: 'Vietnam',
    VG: 'Virgin Islands (British)',
    VI: 'Virgin Islands (U.S.)',
    WF: 'Wallis and Futuna Islands',
    EH: 'Western Sahara',
    YE: 'Yemen',
    ZR: 'Zaire',
    ZM: 'Zambia',
    ZW: 'Zimbabwe'
  };
}

export function getStateOptions(country = null) {
  const allStatesAndProvinces = {
    US: {
      AL: 'Alabama',
      AK: 'Alaska',
      AS: 'American Samoa',
      AZ: 'Arizona',
      AR: 'Arkansas',
      CA: 'California',
      CO: 'Colorado',
      CT: 'Connecticut',
      DE: 'Delaware',
      DC: 'District of Columbia',
      FM: 'Federated States of Micronesia',
      FL: 'Florida',
      GA: 'Georgia',
      GU: 'Guam',
      HI: 'Hawaii',
      ID: 'Idaho',
      IL: 'Illinois',
      IN: 'Indiana',
      IA: 'Iowa',
      KS: 'Kansas',
      KY: 'Kentucky',
      LA: 'Louisiana',
      ME: 'Maine',
      MH: 'Marshall Islands',
      MD: 'Maryland',
      MA: 'Massachusetts',
      MI: 'Michigan',
      MN: 'Minnesota',
      MS: 'Mississippi',
      MO: 'Missouri',
      MT: 'Montana',
      NE: 'Nebraska',
      NV: 'Nevada',
      NH: 'New Hampshire',
      NJ: 'New Jersey',
      NM: 'New Mexico',
      NY: 'New York',
      NC: 'North Carolina',
      ND: 'North Dakota',
      MP: 'Northern Mariana Islands',
      OH: 'Ohio',
      OK: 'Oklahoma',
      OR: 'Oregon',
      PW: 'Palau',
      PA: 'Pennsylvania',
      PR: 'Puerto Rico',
      RI: 'Rhode Island',
      SC: 'South Carolina',
      SD: 'South Dakota',
      TN: 'Tennessee',
      TX: 'Texas',
      UT: 'Utah',
      VT: 'Vermont',
      VI: 'Virgin Island',
      VA: 'Virginia',
      WA: 'Washington',
      WV: 'West Virginia',
      WI: 'Wisconsin',
      WY: 'Wyoming'
    },
    CA: {
      AB: 'Alberta',
      BC: 'British Columbia',
      MB: 'Manitoba',
      NB: 'New Brunswick',
      NL: 'Newfoundland and Labrador',
      NT: 'Northwest Territories',
      NS: 'Nova Scotia',
      NU: 'Nunavut',
      ON: 'Ontario',
      PE: 'Prince Edward Island',
      QC: 'Quebec',
      SK: 'Saskatchewan',
      YT: 'Yukon Territory'
    }
  };

  const countryIds = Object.keys(allStatesAndProvinces);

  if (country && countryIds.includes(country))
    return allStatesAndProvinces[country];

  const allRegions = {};
  countryIds.forEach(id => {
    Object.assign(allRegions, allStatesAndProvinces[id]);
  });

  return allRegions;
}

export function getCountryIdByName(name) {
  if (!name) return null;

  const trimmedName = name.trim().toLowerCase();

  const countryEntry = Object.entries(getCountryOptions()).find(
    ([, value]) => value.toLowerCase() === trimmedName
  );

  if (countryEntry) return countryEntry[0];

  throw new Error('Country name not found');
}
